/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExpandMore, Warning } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import Alert from 'components/Alert';
import { CustomContainer } from 'components/CustomContainer';
import { GridSkeleton } from 'components/Grid';
import { NONE } from 'components/MeasurementDates/constants';
import { RolloverDefaults } from 'components/MeasurementDates/types';
import companyParser from 'components/MeasurementDates/utilities/parser/parser';
import { RolloverAttribute } from 'components/MeasurementDates/utilities/parser/parser.types';
import ScalarDatasheet from 'components/ScalarDatasheet';
import { useMDContext } from 'context/MDContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { CompanyRollover } from 'services/hooks/useRolloverCompanies/rollover.types';
import theme from 'theme';
import { getObjectValue } from 'utilities';
import {
  getMeasurementDateInfo,
  getMeasurementDateMismatchMessage,
  MOST_RECENT_DATE,
} from './CompaniesTable.constants';
import { CompanyChangeType } from './CompaniesTable.types';

const ROLLOVER_COMPANIES_CONTAINER = 'rollover-companies-container';

const CompaniesTable = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = storeValue;
  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const {
    rolloverDetailCompanies,
    setRolloverDetailCompanies,
    selectedFundNames,
    measurementDateOptionsByCompanyId,
    latestPossibleMarketDate,
    marketDate,
  } = useMDContext();

  const [defaultState, setDefaultState] = useState<RolloverDefaults>({
    excludeValuation: true,
    excludeDocuments: true,
    resetFinancials: true,
    keepMarketData: true,
    marketDate: marketDate as string,
    customDate: MOST_RECENT_DATE,
  });

  // create a memoized set of date options from the rollover detail companies
  const dateOptions = useMemo(() => {
    const optionsSet = new Set(
      rolloverDetailCompanies.flatMap(company => measurementDateOptionsByCompanyId[company.companyId] ?? [])
    );
    optionsSet.delete(NONE);
    const options = Array.from(optionsSet).sort((a, b) => moment(b).diff(moment(a)));
    options.unshift(MOST_RECENT_DATE, NONE);
    return options;
  }, [measurementDateOptionsByCompanyId, rolloverDetailCompanies]);

  // this hook is essentially a sync hook that handles syncing the default state with the selected companies
  useEffect(() => {
    setDefaultState(prevState =>
      rolloverDetailCompanies.reduce(
        (acc: RolloverDefaults, company: CompanyRollover) => {
          acc.excludeValuation = acc.excludeValuation && company.excludeValuation;
          acc.excludeDocuments = acc.excludeDocuments && company.excludeDocuments;
          acc.resetFinancials = acc.resetFinancials && company.resetFinancials;
          acc.keepMarketData = acc.keepMarketData && company.keepMarketData;
          return acc;
        },
        {
          excludeValuation: true,
          excludeDocuments: true,
          resetFinancials: true,
          keepMarketData: true,
          marketDate: prevState.marketDate,
          customDate: prevState.customDate,
        }
      )
    );
  }, [rolloverDetailCompanies]);

  // updates the company to match the new value
  const updateCompanyRolloverData = useCallback(
    (company: CompanyRollover, attribute: RolloverAttribute, value: CompanyChangeType) => {
      setRolloverDetailCompanies(companies =>
        companies.map((selectedCompany: CompanyRollover) => {
          if (selectedCompany.companyId === company.companyId) {
            return {
              ...selectedCompany,
              [attribute]: value,
            };
          }
          return selectedCompany;
        })
      );
    },
    [setRolloverDetailCompanies]
  );

  // updates the default state and all companies to match the new value as part of a two way sync
  // this triggers the sync hook to run and update the default state
  const updateCompanyRollovers = useCallback(
    (attribute: RolloverAttribute, value: boolean) => {
      setRolloverDetailCompanies(prevSelectedCompanies =>
        prevSelectedCompanies.map(company => ({
          ...company,
          [attribute]: value,
        }))
      );
    },
    [setRolloverDetailCompanies]
  );

  const updateDefaultMarketDate = useCallback(
    (value: string) => {
      // we update the default state because this has a one way sync with the companies, not a two way sync. i.e. updating just the companies
      // will not update the default state.
      setDefaultState(prevState => ({
        ...prevState,
        marketDate: value,
      }));
      setRolloverDetailCompanies(prevRolloverCompanies =>
        prevRolloverCompanies.map((company: CompanyRollover) => ({
          ...company,
          marketDate: value,
        }))
      );
    },
    [setRolloverDetailCompanies, setDefaultState]
  );

  // updates the default and company states
  const updateCompanyRolloverCustomDate = useCallback(
    (value: string | null) => {
      setDefaultState(prevState => ({
        ...prevState,
        customDate: value,
      }));
      setRolloverDetailCompanies(prevRolloverCompanies =>
        prevRolloverCompanies.map((company: CompanyRollover) => {
          let customDate;
          let warning = false;
          const customDateOptions = measurementDateOptionsByCompanyId[company.companyId] ?? [];
          if (customDateOptions.length === 0) {
            throw new Error('No custom date options found for company');
          }
          if (value === MOST_RECENT_DATE) {
            [, customDate] = customDateOptions;
          } else {
            // look for the customDate option, otherwise leave it as is, but add a warning
            customDate = customDateOptions?.find((date: string) => date === value);
            if (!customDate) {
              customDate = company.customDate;
              warning = true;
            }
          }
          return {
            ...company,
            warning,
            customDate,
          };
        })
      );
    },
    [setRolloverDetailCompanies, measurementDateOptionsByCompanyId]
  );

  const grid = useMemo<Array<Array<ICell>>>(
    () =>
      companyParser({
        areFundInvestmentsEnabled,
        dateOptions,
        defaultState,
        latestPossibleMarketDate,
        measurementDateOptionsByCompanyId,
        rolloverDetailCompanies,
        updateCompanyRolloverCustomDate,
        updateCompanyRolloverData,
        updateCompanyRollovers,
        updateDefaultMarketDate,
      }),
    [
      areFundInvestmentsEnabled,
      dateOptions,
      defaultState,
      latestPossibleMarketDate,
      measurementDateOptionsByCompanyId,
      rolloverDetailCompanies,
      updateCompanyRolloverCustomDate,
      updateCompanyRolloverData,
      updateCompanyRollovers,
      updateDefaultMarketDate,
    ]
  );

  const measurementDateInfo = useMemo(
    () =>
      getMeasurementDateInfo({
        areFundInvestmentsEnabled,
        companyCount: rolloverDetailCompanies.length,
        fundCount: selectedFundNames.length,
      }),
    [selectedFundNames.length, rolloverDetailCompanies.length, areFundInvestmentsEnabled]
  );

  const companiesHaveWarnings = useMemo(
    () => rolloverDetailCompanies.some(company => company.warning),
    [rolloverDetailCompanies]
  );

  if (isEmpty(grid)) {
    return <GridSkeleton />;
  }

  // Because we are just using the datasheet, I don't think we have to provide an array of ICell.
  // that probably means we need to adjust what is specifically required for the scalar datasheet
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
        <Typography variant="body1" gutterBottom>
          {measurementDateInfo}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <CustomContainer rowSpacing={2} mt={theme.spacing(3)}>
          {companiesHaveWarnings && (
            <Grid size={{ xs: 12 }}>
              <Alert isAlertVisible={companiesHaveWarnings} severity="warning" icon={<Warning />}>
                {getMeasurementDateMismatchMessage(areFundInvestmentsEnabled)}
              </Alert>
            </Grid>
          )}

          <Grid className={ROLLOVER_COMPANIES_CONTAINER} size={{ xs: 12 }}>
            <ScalarDatasheet className="data-sheet" data={grid} />
          </Grid>
        </CustomContainer>
      </AccordionDetails>
    </Accordion>
  );
};

export default CompaniesTable;
